import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '~/components/button';
import { api } from '~/lib/api';

export const Route = createFileRoute('/_authed/founder/calendar')({
  component: CalendarPage,
});

const PASTE_EXAMPLE = `[
  { "start": "2026-05-13T09:00:00Z", "end": "2026-05-13T10:00:00Z", "attendees": 3 },
  { "start": "2026-05-13T10:00:00Z", "end": "2026-05-13T11:30:00Z", "attendees": 2 },
  { "start": "2026-05-13T13:00:00Z", "end": "2026-05-13T15:00:00Z", "attendees": 0 }
]`;

function CalendarPage() {
  const qc = useQueryClient();
  const [pasteText, setPasteText] = useState('');
  const [pasteError, setPasteError] = useState<string | null>(null);
  const summary = useQuery({ queryKey: ['calendar-summary'], queryFn: api.calendarSummary });

  const paste = useMutation({
    mutationFn: api.calendarManualPaste,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['calendar-summary'] });
      setPasteText('');
    },
  });

  const apiBase = import.meta.env.VITE_API_URL || '';

  return (
    <section className="mx-auto max-w-2xl px-4 py-12">
      <h1 className="text-2xl font-semibold tracking-tight">calendar shape</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        we read times and attendee counts only. titles, descriptions, attendees — never.
      </p>

      <section className="mt-8 rounded-[var(--radius)] border border-border p-4">
        <h2 className="text-sm font-medium">connect google calendar</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          read-only scope. click connect — google's screen will confirm permissions.
        </p>
        <div className="mt-3">
          <a href={`${apiBase}/api/founder/calendar/google/start`}>
            <Button size="sm">connect google</Button>
          </a>
        </div>
      </section>

      <section className="mt-6 rounded-[var(--radius)] border border-border p-4">
        <h2 className="text-sm font-medium">paste shape (dev / fallback)</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          works offline. JSON array of {`{ start, end, attendees }`}.
        </p>
        <textarea
          value={pasteText}
          onChange={(e) => setPasteText(e.target.value)}
          rows={8}
          placeholder={PASTE_EXAMPLE}
          className="mt-3 w-full font-mono text-xs rounded-[var(--radius)] border border-input bg-background p-3"
        />
        {pasteError && <p className="mt-2 text-xs text-destructive">{pasteError}</p>}
        <div className="mt-3">
          <Button
            size="sm"
            disabled={paste.isPending || !pasteText.trim()}
            onClick={() => {
              setPasteError(null);
              try {
                const arr = JSON.parse(pasteText);
                if (!Array.isArray(arr)) throw new Error('expected an array');
                paste.mutate(arr);
              } catch (err) {
                setPasteError((err as Error).message);
              }
            }}
          >
            {paste.isPending ? 'saving…' : 'ingest'}
          </Button>
        </div>
      </section>

      <section className="mt-8">
        <h2 className="text-sm uppercase tracking-wide text-muted-foreground">
          next 14 days · {summary.data?.totalEvents ?? 0} events
        </h2>
        <ul className="mt-3 space-y-2">
          {(summary.data?.days ?? []).map((d) => (
            <li key={d.date} className="rounded-[var(--radius)] border border-border p-3 text-sm">
              <div className="flex items-baseline justify-between">
                <span className="font-medium">{d.date}</span>
                <span className="text-xs text-muted-foreground">
                  {Math.round(d.minutesBooked / 60)}h booked · {d.meetingCount} meetings · {d.blockCount} blocks
                </span>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </section>
  );
}
